import { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Chart } from '@/components/Chart';
import {
  SalesHourlyBucket,
  SalesTrendBucket
} from '@/types/SalesSummary.interface';
import {
  formatCount,
  formatMoney,
  formatTrendLabel
} from '@/utils/salesReport';

interface ReportBarChartProps {
  categories: string[];
  values: number[];
  transactions: number[];
  color: string;
  seriesName: string;
  height?: number;
}

const ReportBarChart = ({
  categories,
  values,
  transactions,
  color,
  seriesName,
  height = 260
}: ReportBarChartProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const options = useMemo(
    () => ({
      chart: {
        background: 'transparent',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: theme.typography.fontFamily
      },
      colors: [color],
      dataLabels: { enabled: false },
      theme: { mode: theme.palette.mode },
      plotOptions: { bar: { borderRadius: 3, columnWidth: '70%' } },
      xaxis: {
        categories,
        labels: {
          style: { colors: theme.palette.text.secondary, fontSize: '10px' },
          rotate: -45,
          hideOverlappingLabels: true
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: {
          style: { colors: theme.palette.text.secondary, fontSize: '10px' },
          formatter: (val: number) =>
            `₺${val.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`
        }
      },
      tooltip: {
        theme: theme.palette.mode,
        y: {
          formatter: (val: number, opts: { dataPointIndex: number }) =>
            `${formatMoney(val)} · ${t('salesReport.transactions', {
              n: formatCount(transactions[opts.dataPointIndex] ?? 0)
            })}`
        }
      },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 5 }
    }),
    [categories, color, theme, transactions, t]
  );

  return (
    <Box sx={{ width: '100%' }} data-testid="report-chart">
      <Chart
        options={options}
        series={[{ name: seriesName, data: values }]}
        type="bar"
        height={height}
      />
    </Box>
  );
};

export const HourlySalesChart = ({
  hourly,
  seriesName
}: {
  hourly: SalesHourlyBucket[];
  seriesName: string;
}) => {
  const theme = useTheme();
  return (
    <ReportBarChart
      categories={hourly.map((h) => `${String(h.hour).padStart(2, '0')}:00`)}
      values={hourly.map((h) => h.sales)}
      transactions={hourly.map((h) => h.transactions)}
      color={theme.palette.warning.main}
      seriesName={seriesName}
    />
  );
};

export const SalesTrendBars = ({
  trend,
  seriesName
}: {
  trend: SalesTrendBucket[];
  seriesName: string;
}) => {
  const theme = useTheme();
  return (
    <ReportBarChart
      categories={trend.map((d) => formatTrendLabel(d.date))}
      values={trend.map((d) => d.total)}
      transactions={trend.map((d) => d.transactions)}
      color={theme.palette.info.main}
      seriesName={seriesName}
    />
  );
};
